/**
 * Mobile Quick Add Drawer Web Component
 *
 * Architecture — maximum Liquid, minimum JS:
 *   - One <mobile-quick-add-drawer> shell lives on the page (static Liquid).
 *   - Each product card contains a <template data-mqad-template> element with
 *     ALL drawer content pre-rendered by Liquid:
 *       • Swiper slides with proper image_tag CDN URLs
 *       • Price HTML formatted by Shopify `money` filter
 *       • One variant button per variant with pre-formatted prices in data-*
 *       • Availability disabled state set at render time
 *   - On trigger click: JS clones the template's DocumentFragment and slots
 *     the nodes into the shell — no HTML building, no JSON parsing.
 *   - On variant click: price comes from data-variant-price (Liquid-formatted).
 *   - Swiper.js handles the image carousel.
 */
(() => {
  if (customElements.get('mobile-quick-add-drawer')) return;

  class MobileQuickAddDrawer extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onTriggerPointerEnter = this.onTriggerPointerEnter.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onOptionClick = this.onOptionClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.swiperInstance = null;
    }

    connectedCallback() {
      this.cacheDOM();
      this.bindEvents();
    }

    disconnectedCallback() {
      this.destroySwiper();
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onTriggerClick);
      document.removeEventListener('pointerenter', this.onTriggerPointerEnter, true);
    }

    /* ----- DOM cache ----- */
    cacheDOM() {
      this.panel = this.querySelector('.mqad__panel');
      this.titleEl = this.querySelector('[data-mqad-title]');
      this.priceEl = this.querySelector('[data-mqad-price]');
      this.slidesContainer = this.querySelector('[data-mqad-slides]');
      this.optionsContainer = this.querySelector('[data-mqad-options]');
      this.form = this.querySelector('[data-mqad-form]');
      this.variantInput = this.querySelector('[data-mqad-variant-input]');
      this.submitBtn = this.querySelector('[data-mqad-submit]');
      this.submitText = this.querySelector('[data-mqad-submit-text]');
      this.submitCheck = this.querySelector('[data-mqad-submit-check]');
      this.detailsLink = this.querySelector('[data-mqad-details-link]');
      this.swiperEl = this.querySelector('.mqad__swiper');
      this.closeButtons = this.querySelectorAll('[data-mqad-close]');
    }

    /* ----- Events ----- */
    bindEvents() {
      document.addEventListener('click', this.onTriggerClick);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('pointerenter', this.onTriggerPointerEnter, true);
      this.closeButtons.forEach((btn) => btn.addEventListener('click', this.onCloseClick));
      this.optionsContainer.addEventListener('click', this.onOptionClick);
      this.form.addEventListener('submit', this.onSubmit);
    }

    /* ----- Prefetch first image on hover/touch-start (zero page-load cost) ----- */
    onTriggerPointerEnter(event) {
      const trigger = event.target.closest('.mqad-trigger');
      if (!trigger || trigger._mqadPrefetched) return;
      trigger._mqadPrefetched = true;

      const tpl = trigger.closest('.Card--product')?.querySelector('[data-mqad-template]');
      if (!tpl) return;

      const firstImg = tpl.content.querySelector('[data-slot="slides"] img');
      if (firstImg?.src) {
        const preload = new Image();
        preload.src = firstImg.src;
      }
    }

    /* ----- Trigger from product card ----- */
    onTriggerClick(event) {
      const trigger = event.target.closest('.mqad-trigger');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();

      const card = trigger.closest('.Card--product');
      if (!card) return;

      const tpl = card.querySelector('[data-mqad-template]');
      if (!tpl) return;

      this.stampFromTemplate(tpl);
      const firstImg = this.slidesContainer.querySelector('img');
      if (firstImg && !firstImg.complete) {
        firstImg.decode()
          .then(() => this.open())
          .catch(() => this.open()); /* open anyway on decode error */
      } else {
        this.open();
      }
    }

    /* ----- Stamp Liquid-pre-rendered content into the shell ----- */
    stampFromTemplate(tpl) {
      this.resetSubmitButton();
      this.variantInput.value = '';
      this.setSubmitDisabled(true);

      /* Simple strings stored as data attributes on the <template> element */
      this.titleEl.textContent = tpl.dataset.title || '';
      this.detailsLink.href = tpl.dataset.url   || '#';

      /* Clone the inert DocumentFragment — images won't load until appended */
      const frag = tpl.content.cloneNode(true);

      /* Slot: swiper slides */
      const slidesSlot = frag.querySelector('[data-slot="slides"]');
      this.destroySwiper();
      this.slidesContainer.innerHTML = '';
      if (slidesSlot) {
        while (slidesSlot.firstChild) this.slidesContainer.appendChild(slidesSlot.firstChild);
      }

      /* Slot: formatted price HTML */
      const priceSlot = frag.querySelector('[data-slot="price"]');
      this.priceEl.innerHTML = '';
      if (priceSlot) {
        while (priceSlot.firstChild) this.priceEl.appendChild(priceSlot.firstChild);
      }

      /* Slot: variant option buttons */
      const optionsSlot = frag.querySelector('[data-slot="options"]');
      this.optionsContainer.innerHTML = '';
      if (optionsSlot) {
        while (optionsSlot.firstChild) this.optionsContainer.appendChild(optionsSlot.firstChild);
      }

      /* Slot: single default variant (products with no real option choices) */
      const defaultSlot = frag.querySelector('[data-slot="default-variant"]');
      if (defaultSlot) {
        this.variantInput.value = defaultSlot.dataset.id || '';
        this.setSubmitDisabled(defaultSlot.dataset.available !== 'true');
      }

      /* Init Swiper after DOM paint so slide widths are computed correctly */
      requestAnimationFrame(() => {
        if (typeof Swiper !== 'undefined') {
          this.swiperInstance = new Swiper(this.swiperEl, {
            slidesPerView: 1.09,
            spaceBetween: 8,
            grabCursor: true,
            breakpoints: {
              525: {
                slidesPerView: 1.5
              }
            }
          });
        }
      });
    }

    /* ----- Variant selection ----- */
    onOptionClick(event) {
      const btn = event.target.closest('.mqad__option-btn');
      if (!btn || btn.disabled) return;

      /* Deselect all */
      this.optionsContainer.querySelectorAll('.mqad__option-btn').forEach((b) => {
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-checked', 'false');
      });

      /* Select clicked */
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-checked', 'true');

      /* Update hidden input */
      this.variantInput.value = btn.dataset.variantId;

      /* Update price — values are pre-formatted by Liquid's `money` filter */
      const price = btn.dataset.variantPrice || '';
      const comparePrice = btn.dataset.variantComparePrice || '';
      if (comparePrice) {
        this.priceEl.innerHTML =
          '<span class="mqad__price-sale">' + price + '</span>' +
          '<span class="mqad__price-compare">' + comparePrice + '</span>';
      } else {
        this.priceEl.textContent = price;
      }

      /* Only reachable when button is not disabled → variant is available */
      this.setSubmitDisabled(false);
      this.submitText.textContent = 'Add to Cart';
    }

    /* ----- Submit / Add to Cart ----- */
    onSubmit(event) {
      event.preventDefault();
      if (!this.variantInput.value || this.submitBtn.disabled) return;

      this.submitBtn.classList.add('mqad__add-btn--loading');
      this.submitText.style.display = 'none';
      this.submitCheck.style.display = 'inline-flex';

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(this.form),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then((data) => {
          this.submitBtn.classList.remove('mqad__add-btn--loading');
          this.close();
          setTimeout(() => {
            document.body.dispatchEvent(new CustomEvent('cart.productAdded', { detail: data }));
            const toggle = document.querySelector('.MiniCart__toggle');
            if (toggle) toggle.click();
          }, 400);
        })
        .catch((err) => {
          console.error('[MobileQuickAddDrawer] Add to cart error:', err);
          this.submitBtn.classList.remove('mqad__add-btn--loading');
          this.submitText.style.display = '';
          this.submitCheck.style.display = 'none';
          this.submitText.textContent = 'Error – Try Again';
          this.setSubmitDisabled(false);
          setTimeout(() => { this.submitText.textContent = 'Add to Cart'; }, 2000);
        });
    }

    resetSubmitButton() {
      this.submitText.style.display = '';
      this.submitCheck.style.display = 'none';
      this.submitBtn.classList.remove('mqad__add-btn--success', 'mqad__add-btn--loading');
      this.submitText.textContent = 'Add to Cart';
    }

    /* ----- Viewport height tracking (fixes Chrome bottom-bar gap) ----- */
    bindViewport() {
      this.onViewportResize = () => {
        const h = (window.visualViewport ? window.visualViewport.height : window.innerHeight);
        this.style.setProperty('--mqad-vh', h + 'px');
      };
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportResize);
      } else {
        window.addEventListener('resize', this.onViewportResize);
      }
      this.onViewportResize();
    }

    releaseViewport() {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onViewportResize);
      } else {
        window.removeEventListener('resize', this.onViewportResize);
      }
    }

    /* ----- Open / Close ----- */
    open() {
      /* Robust scroll lock: position:fixed preserves layout and blocks all touch scroll */
      this._scrollY = window.scrollY;
      document.body.style.cssText += ';position:fixed;top:-' + this._scrollY + 'px;left:0;right:0;overflow-y:scroll;';
      // this.bindViewport();
      this.setAttribute('aria-hidden', 'false');
      this.trapFocus();
    }

    close() {
      /* Restore scroll position exactly */
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflowY = '';
      window.scrollTo({ top: this._scrollY, behavior: 'instant' });
      // this.releaseViewport();
      this.setAttribute('aria-hidden', 'true');
      this.releaseFocus();
    }

    onCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
    }

    /* ----- Focus trap ----- */
    trapFocus() {
      const focusable = this.panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length > 0) {
        this._previouslyFocused = document.activeElement;
        focusable[0].focus();
      }
    }

    releaseFocus() {
      if (this._previouslyFocused) {
        this._previouslyFocused.focus();
        this._previouslyFocused = null;
      }
    }

    /* ----- Swiper teardown ----- */
    destroySwiper() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    /* ----- Helpers ----- */
    setSubmitDisabled(disabled) {
      this.submitBtn.disabled = disabled;
      this.submitBtn.setAttribute('aria-disabled', String(disabled));
    }
  }

  customElements.define('mobile-quick-add-drawer', MobileQuickAddDrawer);
})();